import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setIntendedUrl } from "~/slices/auth";
import type { AppDispatch, RootState } from "~/store";

export interface AuthIds {
  userId: string;
  organizationId: string;
}

export interface UseAuthGuardOptions {
  redirect?: boolean;
}

export interface UseAuthGuardReturn {
  isAuthenticated: boolean;
  ids: AuthIds | null;
  ensure: () => AuthIds | null;
}

/**
 * 認証状態をチェックし、必要に応じてリダイレクトを行うカスタムフック
 * currentUser.idとcurrentUser.organization_idの存在チェックを共通化
 */
export const useAuthGuard = (options: UseAuthGuardOptions = {}): UseAuthGuardReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser, status } = useSelector((state: RootState) => state.auth);

  const { redirect = true } = options;

  // 認証済みの場合のID情報を計算
  const ids: AuthIds | null = useMemo(() => {
    if (currentUser?.id && currentUser?.organization_id) {
      return {
        userId: currentUser.id,
        organizationId: currentUser.organization_id,
      };
    }
    return null;
  }, [currentUser?.id, currentUser?.organization_id]);

  const isAuthenticated = !!ids;

  // 認証チェックを行い、必要に応じてリダイレクトする
  const ensure = useCallback((): AuthIds | null => {
    if (!ids) {
      if (redirect && status === "idle") {
        // 現在のパスを取得してintendedUrlに設定
        const currentPath = location.pathname + location.search + location.hash;
        if (currentPath !== "/signin" && currentPath !== "/signup") {
          dispatch(setIntendedUrl(currentPath));
        }
        navigate("/signin");
      }
      return null;
    }
    return ids;
  }, [ids, redirect, status, navigate, location, dispatch]);

  return {
    isAuthenticated,
    ids,
    ensure,
  };
};
